import React, { useState, useEffect } from 'react';
import ApiService from '../../services/apiService';
import './NotificationManager.css';

const NotificationManager = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    category: 'GENERAL',
    linkType: 'url',
    linkValue: '',
    pdf: null,
    image: null
  });

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAllNotifications();
      setNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'file' ? files[0] : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = {
        title: formData.title,
        date: formData.date,
        category: formData.category,
        linkType: formData.linkType,
        linkValue: formData.linkType === 'url' ? formData.linkValue : '',
        ...(formData.linkType === 'pdf' && formData.pdf && { pdf: formData.pdf }),
        ...(formData.linkType === 'image' && formData.image && { image: formData.image })
      };

      await ApiService.createNotification(submitData);

      await fetchNotifications();
      resetForm();
    } catch (error) {
      console.error('Error saving notification:', error);
      alert('Error saving notification: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this notification?')) {
      try {
        await ApiService.deleteNotification(id);
        await fetchNotifications();
      } catch (error) {
        console.error('Error deleting notification:', error);
        alert('Error deleting notification: ' + error.message);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      date: '',
      category: 'GENERAL',
      linkType: 'url',
      linkValue: '',
      pdf: null,
      image: null
    });
    setShowForm(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return <div className="loading">Loading notifications...</div>;
  }

  return (
    <div className="notification-manager">
      <div className="manager-header">
        <h2>Notification Management</h2>
        <button 
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
        >
          {showForm ? 'Cancel' : 'Add New Notification'}
        </button>
      </div>

      {showForm && (
        <div className="notification-form">
          <h3>Add New Notification</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Notification Title:</label>
              <textarea
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                rows="3"
                placeholder="Enter notification title..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="date">Date:</label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Category:</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                <option value="GENERAL">General</option>
                <option value="TENDERS">Tenders</option>
                <option value="EXAMINATION">Examination</option>
                <option value="CIRCULARS">Circulars</option>
                <option value="PRESS NOTES">Press Notes</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="linkType">Link Type:</label>
              <select
                id="linkType"
                name="linkType"
                value={formData.linkType}
                onChange={handleInputChange}
                required
              >
                <option value="url">URL Link</option>
                <option value="pdf">PDF File</option>
                <option value="image">Image File</option>
              </select>
            </div>

            {formData.linkType === 'url' && (
              <div className="form-group">
                <label htmlFor="linkValue">URL:</label>
                <input
                  type="url"
                  id="linkValue"
                  name="linkValue"
                  value={formData.linkValue}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                  required
                />
              </div>
            )}

            {formData.linkType === 'pdf' && (
              <div className="form-group">
                <label htmlFor="pdf">PDF File:</label>
                <input
                  type="file"
                  id="pdf"
                  name="pdf"
                  onChange={handleInputChange}
                  accept=".pdf"
                  required
                />
              </div>
            )}

            {formData.linkType === 'image' && (
              <div className="form-group">
                <label htmlFor="image">Image File:</label>
                <input
                  type="file"
                  id="image"
                  name="image"
                  onChange={handleInputChange}
                  accept="image/*"
                  required
                />
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-success">
                Create Notification
              </button>
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="notifications-list">
        <h3>Existing Notifications ({notifications.length})</h3>
        <div className="table-container">
          <table className="notifications-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Date</th>
                <th>Category</th>
                <th>Status</th>
                <th>Link Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {notifications.map(notification => (
                <tr key={notification.id}>
                  <td>
                    <div className="notification-title">
                      {notification.title}
                    </div>
                  </td>
                  <td>{formatDate(notification.date)}</td>
                  <td>
                    <span className="category-badge">{notification.category}</span>
                  </td>
                  <td>
                    <span className={`status-badge ${notification.is_active ? 'active' : 'inactive'}`}>
                      {notification.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <span className="link-type">{notification.link_type}</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        onClick={() => handleDelete(notification.id)}
                        className="btn btn-sm btn-danger"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {notifications.length === 0 && (
                <tr>
                  <td colSpan="6" className="no-data">No notifications found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NotificationManager;